import { condition, type FilterNode } from '@ofmos/shared/filter-ast';
import type { FieldMetadata, InboundRelation, ObjectMetadata } from '../../../data/client';

/**
 * WHICH RELATED SECTIONS A RECORD PAGE SHOWS, and what each one queries.
 *
 * Pure functions over the inbound-relations read (D2), so the two things a related
 * panel gets wrong - showing the wrong records, and rendering two sections a user
 * cannot tell apart - are answerable by a unit test instead of by opening a page.
 *
 * A SECTION IS A FILTERED QUERY AGAINST THE CHILD (D1). Never a reverse-side filter:
 * the query planner refuses those by design, and the child already owns a real uuid
 * column pointing back here. Nothing in this file ever selects a MANY_TO_MANY field.
 *
 * The planner now HAS the reverse-side filter
 * (`docs/decisions/relation-filters.md` D2), and this file still does not want it. A
 * section asks about ONE parent, and a plain condition on the child's own uuid column
 * answers that in a single index lookup; an EXISTS here would be slower and would say the
 * same thing.
 */

/** How a section is rendered and written to. */
export type RelatedSectionKind =
  /** An ordinary child object: rows open their own record, "Add" creates one. */
  | 'child'
  /**
   * An N:N membership through a junction (D3). The row is a junction record but it
   * LINKS TO THE OTHER ENDPOINT, because the junction's own record page is a dead end
   * (D7): "Untitled", four fields, and no endpoint on it.
   */
  | 'junction';

export interface RelatedSection {
  /** Stable across renames: the relation field's uuid (INV-7). */
  readonly id: string;
  readonly kind: RelatedSectionKind;
  /** "Leads", or "Leads (Setter)" when one child points here more than once. */
  readonly title: string;
  /** The object the section QUERIES - the child, or the junction. */
  readonly object: ObjectMetadata;
  /** The child's own column pointing at this record. The filter is built on it. */
  readonly field: FieldMetadata;
  /**
   * `junction` only: the object on the OTHER side of the membership. A row renders and
   * links to a record of THIS object, never to the junction row.
   */
  readonly otherEndpointObjectId?: string;
  /** `junction` only: the MANY_TO_MANY field a link is written through (D5). */
  readonly relationFieldId?: string;
  /**
   * `junction` only: is THIS record the source of the membership, or the target?
   *
   * The link route takes `{sourceId, targetId}` and the record layer checks each
   * against its endpoint's object, so getting this backwards is a 4xx on every add
   * from the target side. Derived once here rather than at the call site.
   */
  readonly parentIsSource?: boolean;
}

/**
 * THE SECTION SET.
 *
 * The server already ordered the relations `(nav-group rank, child position, field
 * position)` and already dropped hidden and archived children, so this PRESERVES the
 * array order rather than re-deriving it: two implementations of one total order is
 * how two surfaces start disagreeing about which section comes first.
 *
 * A JUNCTION IS RECOGNISED BY ITS OBJECT, not by its field. Its endpoint fields are
 * ordinary MANY_TO_ONE fields - that is why the read returns them at all - and what
 * makes the section a membership is that the object owning them is a junction whose
 * settings name the MANY_TO_MANY field a link has to go through.
 *
 * AN UNPROMOTED JUNCTION PRODUCES NO SECTION, and that is not a bug here: the engine
 * mints junctions hidden, the read drops hidden objects, so a user-created N:N shows
 * nothing until an admin promotes it ("Show in navigation"). Stated rather than
 * silently worked around, because the honest fix is a platform change D7 puts out of
 * scope.
 */
export function relatedSections(relations: readonly InboundRelation[]): readonly RelatedSection[] {
  const duplicated = duplicatedObjectIds(relations);

  return relations.map((relation) => {
    const title = sectionTitle(relation, duplicated.has(relation.object.id));
    const junction = relation.object.junction;
    if (junction === undefined) {
      return {
        id: relation.field.id,
        kind: 'child',
        title,
        object: relation.object,
        field: relation.field,
      };
    }
    /**
     * WHICH SIDE THIS RECORD IS ON. A junction joins two objects and the parent may be
     * either, so reading `sourceObjectId` blindly would name the wrong other side on
     * every target-side page. The endpoint fields carry fixed names (`expand.ts`
     * chooses them precisely so they survive a rename of either endpoint), which is
     * what makes this readable rather than a guess.
     */
    const parentIsSource = relation.field.name === 'source';
    return {
      id: relation.field.id,
      kind: 'junction',
      title,
      object: relation.object,
      field: relation.field,
      otherEndpointObjectId: parentIsSource ? junction.targetObjectId : junction.sourceObjectId,
      relationFieldId: junction.relationFieldId,
      parentIsSource,
    };
  });
}

/**
 * PER-FIELD NAMING, and only where it is needed (D2).
 *
 * `team_member` receives six inbound relations from four objects in the seeded model -
 * `lead.setter`, `lead.closer`, `deal.setter`, `deal.closer` among them - so a page
 * named per OBJECT would render "Leads" twice and "Deals" twice with nothing telling
 * them apart. Where a child points here through exactly ONE field the plain label is
 * the better name: "(Agency)" on the only Creators section is noise.
 */
function sectionTitle(relation: InboundRelation, ambiguous: boolean): string {
  return ambiguous
    ? `${relation.object.labelPlural} (${relation.field.label})`
    : relation.object.labelPlural;
}

function duplicatedObjectIds(relations: readonly InboundRelation[]): ReadonlySet<string> {
  const seen = new Set<string>();
  const twice = new Set<string>();
  for (const relation of relations) {
    if (seen.has(relation.object.id)) twice.add(relation.object.id);
    seen.add(relation.object.id);
  }
  return twice;
}

/**
 * The one condition a related list runs on: the child's own relation column equals
 * this record.
 *
 * RELATION's operator set includes `eq` and a MANY_TO_ONE owns a real uuid column, so
 * this is an ordinary filter both backends already speak (INV-6). The junction case is
 * the same shape, because an endpoint field IS a MANY_TO_ONE.
 */
export function relatedFilter(section: RelatedSection, recordId: string): FilterNode {
  return condition(section.field.id, 'eq', recordId);
}

/** How many of the child's own values ride under a related row's title. */
export const RELATED_SECONDARY_FIELDS = 3;

/**
 * The columns a related row shows under its title.
 *
 * Sorted by `position` - the order an admin arranged the object in, and the same order
 * the record page uses - so a related row and the record it opens agree about what
 * matters first.
 *
 * The field the section is FILTERED ON is excluded: every row in a related list holds
 * the same parent, so printing it on each one costs a column and says nothing.
 */
export function relatedSecondaryFields(
  section: RelatedSection,
  fields: readonly FieldMetadata[],
): readonly FieldMetadata[] {
  return fields
    .filter((field) => field.objectId === section.object.id)
    .filter((field) => field.id !== section.field.id)
    .filter((field) => field.id !== section.object.labelIdentifierFieldId)
    .toSorted((a, b) => a.position - b.position)
    .slice(0, RELATED_SECONDARY_FIELDS);
}

/**
 * What a section SELECTS.
 *
 * `endpointFieldIds` is empty for a child section and holds the OTHER endpoint for a
 * junction one. That endpoint is a SYSTEM field and therefore absent from `useFields`;
 * `RecordQuery.select` accepts any field id by design, which is what makes reading it
 * possible at all - and reading it is what turns "Untitled" into "Marie Kern".
 *
 * A junction section with no resolvable endpoint still selects its own fields, so the
 * role and the dates render even when the person cannot be named.
 */
export function relatedSelect(
  section: RelatedSection,
  secondaryFields: readonly FieldMetadata[],
  endpointFieldIds: readonly string[] = [],
): readonly string[] {
  const ids = new Set<string>();
  if (section.object.labelIdentifierFieldId !== '') {
    ids.add(section.object.labelIdentifierFieldId);
  }
  for (const endpoint of endpointFieldIds) ids.add(endpoint);
  for (const field of secondaryFields) ids.add(field.id);
  return [...ids];
}

/**
 * The junction's OTHER endpoint field, found in the inbound relations of the object on
 * the other side.
 *
 * That indirection is the whole trick, and it exists because there is no other door:
 * `listFields` drops system fields at the mapping boundary, so the browser can never
 * ask a junction for its own endpoints - but it CAN ask the far object "which fields
 * point at you", and one of the answers is this junction's endpoint.
 *
 * Returns every match rather than the first: a SELF-JOIN has two endpoints targeting
 * the same object and neither is "the other one" in general, and selecting both costs
 * one column while keeping the row renderable in every case.
 */
export function endpointFieldsOf(
  section: RelatedSection,
  farRelations: readonly InboundRelation[],
): readonly FieldMetadata[] {
  return farRelations
    .filter((relation) => relation.object.id === section.object.id)
    .filter((relation) => relation.field.id !== section.field.id)
    .map((relation) => relation.field);
}
